import type { NodeT, SkeletonDataT } from '../../../types';
import type { VertexT } from '../shape/vertex';
import { VERTEX_TYPE } from '../shape/vertex';
export type BoundingBoxT = {
  centerX: number;
  centerY: number;
  width: number;
  height: number;
};
const getMinX = (bbox: BoundingBoxT): number => bbox.centerX - bbox.width / 2;
const getMaxX = (bbox: BoundingBoxT): number => bbox.centerX + bbox.width / 2;
const getMinY = (bbox: BoundingBoxT): number => bbox.centerY - bbox.height / 2;
const getMaxY = (bbox: BoundingBoxT): number => bbox.centerY + bbox.height / 2;
const BOUNDING_BOX_PADDING = 30;
export const calculateBoundingBox = (points: [number, number][], padding = BOUNDING_BOX_PADDING): BoundingBoxT => {
  if (points.length === 0) {
    return {
      centerX: 0,
      centerY: 0,
      width: 0,
      height: 0
    };
  }
  const minX = Math.min(...points.map(p => p[0])) - padding;
  const minY = Math.min(...points.map(p => p[1])) - padding;
  const maxX = Math.max(...points.map(p => p[0])) + padding;
  const maxY = Math.max(...points.map(p => p[1])) + padding;
  return {
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2,
    width: maxX - minX,
    height: maxY - minY
  };
};
export const getScaleHandleVertices = (bbox: BoundingBoxT): VertexT[] => {
  const minX = getMinX(bbox);
  const maxX = getMaxX(bbox);
  const minY = getMinY(bbox);
  const maxY = getMaxY(bbox);
  return [{
    point: ([minX, minY] as const),
    type: VERTEX_TYPE.SCALE_TOP_LEFT
  }, {
    point: ([bbox.centerX, minY] as const),
    type: VERTEX_TYPE.SCALE_TOP_CENTER
  }, {
    point: ([maxX, minY] as const),
    type: VERTEX_TYPE.SCALE_TOP_RIGHT
  }, {
    point: ([maxX, bbox.centerY] as const),
    type: VERTEX_TYPE.SCALE_CENTER_RIGHT
  }, {
    point: ([maxX, maxY] as const),
    type: VERTEX_TYPE.SCALE_BOTTOM_RIGHT
  }, {
    point: ([bbox.centerX, maxY] as const),
    type: VERTEX_TYPE.SCALE_BOTTOM_CENTER
  }, {
    point: ([minX, maxY] as const),
    type: VERTEX_TYPE.SCALE_BOTTOM_LEFT
  }, {
    point: ([minX, bbox.centerY] as const),
    type: VERTEX_TYPE.SCALE_CENTER_LEFT
  }];
};
export type ScaleTransformT = {
  scaleX: number;
  scaleY: number;
};
export const calculateScaleTransform = (vertexType: (typeof VERTEX_TYPE)[keyof typeof VERTEX_TYPE], originalBBox: BoundingBoxT, newPosition: [number, number]): ScaleTransformT | null => {
  if (originalBBox.width === 0 || originalBBox.height === 0) {
    return null;
  }
  const [newX, newY] = newPosition;
  const halfWidth = originalBBox.width / 2;
  const halfHeight = originalBBox.height / 2;
  switch (vertexType) {
    case VERTEX_TYPE.SCALE_TOP_LEFT:
      {
        const originalX = originalBBox.centerX - halfWidth;
        const originalY = originalBBox.centerY - halfHeight;
        const deltaX = originalX - newX;
        const deltaY = originalY - newY;
        return {
          scaleX: (originalBBox.width + deltaX) / originalBBox.width,
          scaleY: (originalBBox.height + deltaY) / originalBBox.height
        };
      }
    case VERTEX_TYPE.SCALE_TOP_CENTER:
      {
        const originalY = originalBBox.centerY - halfHeight;
        const deltaY = originalY - newY;
        return {
          scaleX: 1,
          scaleY: (originalBBox.height + deltaY) / originalBBox.height
        };
      }
    case VERTEX_TYPE.SCALE_TOP_RIGHT:
      {
        const originalX = originalBBox.centerX + halfWidth;
        const originalY = originalBBox.centerY - halfHeight;
        const deltaX = newX - originalX;
        const deltaY = originalY - newY;
        return {
          scaleX: (originalBBox.width + deltaX) / originalBBox.width,
          scaleY: (originalBBox.height + deltaY) / originalBBox.height
        };
      }
    case VERTEX_TYPE.SCALE_BOTTOM_LEFT:
      {
        const originalX = originalBBox.centerX - halfWidth;
        const originalY = originalBBox.centerY + halfHeight;
        const deltaX = originalX - newX;
        const deltaY = newY - originalY;
        return {
          scaleX: (originalBBox.width + deltaX) / originalBBox.width,
          scaleY: (originalBBox.height + deltaY) / originalBBox.height
        };
      }
    case VERTEX_TYPE.SCALE_BOTTOM_CENTER:
      {
        const originalY = originalBBox.centerY + halfHeight;
        const deltaY = newY - originalY;
        return {
          scaleX: 1,
          scaleY: (originalBBox.height + deltaY) / originalBBox.height
        };
      }
    case VERTEX_TYPE.SCALE_BOTTOM_RIGHT:
      {
        const originalX = originalBBox.centerX + halfWidth;
        const originalY = originalBBox.centerY + halfHeight;
        const deltaX = newX - originalX;
        const deltaY = newY - originalY;
        return {
          scaleX: (originalBBox.width + deltaX) / originalBBox.width,
          scaleY: (originalBBox.height + deltaY) / originalBBox.height
        };
      }
    case VERTEX_TYPE.SCALE_CENTER_RIGHT:
      {
        const originalX = originalBBox.centerX + halfWidth;
        const deltaX = newX - originalX;
        return {
          scaleX: (originalBBox.width + deltaX) / originalBBox.width,
          scaleY: 1
        };
      }
    case VERTEX_TYPE.SCALE_CENTER_LEFT:
      {
        const originalX = originalBBox.centerX - halfWidth;
        const deltaX = originalX - newX;
        return {
          scaleX: (originalBBox.width + deltaX) / originalBBox.width,
          scaleY: 1
        };
      }
    default:
      return null;
  }
};
export const applyScaleTransform = (point: [number, number], transform: ScaleTransformT, anchor: [number, number]): [number, number] => {
  const [x, y] = point;
  const [anchorX, anchorY] = anchor;
  const newX = anchorX + (x - anchorX) * transform.scaleX;
  const newY = anchorY + (y - anchorY) * transform.scaleY;
  return [newX, newY];
};
export const scaleSkeletonData = (skeletonData: SkeletonDataT, transform: ScaleTransformT, anchor: [number, number]): SkeletonDataT => {
  const scaledNodes: NodeT[] = skeletonData.nodes.map(node => ({
    ...node,
    point: applyScaleTransform(node.point, transform, anchor)
  }));
  return {
    nodes: scaledNodes,
    edges: skeletonData.edges,
    templateId: skeletonData.templateId
  };
};
export const isScaleVertexType = (vertexType: (typeof VERTEX_TYPE)[keyof typeof VERTEX_TYPE]): boolean => {
  const scaleTypes: string[] = [VERTEX_TYPE.SCALE_TOP_LEFT, VERTEX_TYPE.SCALE_TOP_CENTER, VERTEX_TYPE.SCALE_TOP_RIGHT, VERTEX_TYPE.SCALE_CENTER_RIGHT, VERTEX_TYPE.SCALE_BOTTOM_RIGHT, VERTEX_TYPE.SCALE_BOTTOM_CENTER, VERTEX_TYPE.SCALE_BOTTOM_LEFT, VERTEX_TYPE.SCALE_CENTER_LEFT];
  return scaleTypes.includes(vertexType);
};